/**
 * myvoice.jsx — Rammy's "My Voice" screen: enrollment + management.
 *
 * Two views, driven by internal state:
 *   1. LIST view — the user's activated voices with Pause/Resume, Re-record,
 *      Delete, Delete-source, Usage-history actions.
 *   2. ENROLL view — the multi-step consented enrollment flow:
 *      (a) Consent — explainer + two affirmative checkboxes
 *      (b) Record — guided challenge prompts, in-browser MediaRecorder
 *      (c) Finalizing — spinner while Deepgram validates + ElevenLabs clones
 *      (d) Preview + activate — user hears their new voice + activates
 *
 * All API traffic uses opaque handles (uuid) — the provider voice_id is never
 * exposed to the client. All calls are authenticated via the existing cookie
 * session.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Mic, StopCircle, Play, Pause, RefreshCw, Trash2, ShieldAlert,
  CheckCircle2, XCircle, Loader2, ChevronLeft, HardDrive, ArrowRight,
  BadgeCheck, MessageSquareWarning,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = (path, opts = {}) =>
  fetch(`${BACKEND}${path}`, { credentials: "include", ...opts });

const jsonPost = (path, body) =>
  api(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });

export function MyVoiceScreen({ go }) {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // "list" | "enroll"
  const [rerecordHandle, setRerecordHandle] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api("/api/myvoice");
      const j = await r.json();
      setVoices(j.voices || []);
    } catch (_) {
      toast.error("Could not load your voices.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (view === "enroll") {
    return (
      <EnrollFlow
        rerecordHandle={rerecordHandle}
        onDone={() => { setView("list"); setRerecordHandle(null); load(); }}
        onBack={() => { setView("list"); setRerecordHandle(null); }}
      />
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="myvoice-screen">
      <button
        onClick={() => go && go("settings")}
        className="text-sm text-[var(--rammy-muted)] hover:text-[var(--rammy-text)] mb-4 inline-flex items-center gap-1"
        data-testid="myvoice-back"
      >
        <ChevronLeft size={14} /> Settings
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">My Voice</h1>
          <p className="text-sm text-[var(--rammy-muted)] mt-1 max-w-lg">
            A private synthetic model of your voice, used only inside your Rammy account.
            You can pause, re-record, or delete it any time.
          </p>
        </div>
        <button
          onClick={() => { setRerecordHandle(null); setView("enroll"); }}
          className="btn-pill btn-primary h-10 px-4 inline-flex items-center gap-2"
          data-testid="myvoice-enroll-cta"
        >
          <Mic size={16} /> Enroll a voice
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--rammy-muted)]">Loading…</div>
      ) : voices.length === 0 ? (
        <EmptyState onStart={() => setView("enroll")} />
      ) : (
        <ul className="space-y-3" data-testid="myvoice-list">
          {voices.map((v) => (
            <VoiceCard key={v.handle} v={v}
              onChange={load}
              onRerecord={(h) => { setRerecordHandle(h); setView("enroll"); }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────
function EmptyState({ onStart }) {
  return (
    <div className="rounded-2xl border border-[var(--rammy-line)] p-8 text-center bg-[var(--rm-surface)]/40" data-testid="myvoice-empty">
      <div className="w-14 h-14 mx-auto rounded-full bg-[var(--rammy-cyan)]/15 flex items-center justify-center">
        <Mic className="text-[var(--rammy-cyan)]" size={22} />
      </div>
      <div className="mt-3 text-lg font-medium">You haven&apos;t enrolled a voice yet</div>
      <div className="mt-1 text-sm text-[var(--rammy-muted)] max-w-md mx-auto">
        Record a short guided script — Rammy will learn your voice and can speak in it for you.
        Any audio generated in your voice is clearly labeled as AI-generated.
      </div>
      <button
        onClick={onStart}
        className="btn-pill btn-primary h-10 px-4 mt-5 inline-flex items-center gap-2"
        data-testid="myvoice-empty-start"
      >
        <Mic size={16} /> Start enrollment
      </button>
    </div>
  );
}

// ── One voice card ─────────────────────────────────────────────────────────
function VoiceCard({ v, onChange, onRerecord }) {
  const [busy, setBusy] = useState(null);
  const [showUsage, setShowUsage] = useState(false);
  const [usage, setUsage] = useState(null);

  const doAction = async (action, path, method = "POST") => {
    setBusy(action);
    try {
      const r = await api(path, { method });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Failed");
      }
      onChange && onChange();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const loadUsage = async () => {
    setShowUsage(true);
    try {
      const r = await api(`/api/myvoice/${v.handle}/usage`);
      const j = await r.json();
      setUsage(j);
    } catch (_) {
      setUsage({ history: [] });
    }
  };

  return (
    <li className="rounded-2xl border border-[var(--rammy-line)] bg-[var(--rm-surface)]/60 p-5"
        data-testid={`myvoice-card-${v.handle}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-lg font-medium">{v.name}</span>
            {v.is_active && !v.is_paused && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 inline-flex items-center gap-1">
                <BadgeCheck size={11} /> Active
              </span>
            )}
            {v.is_paused && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700">Paused</span>
            )}
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--rm-surface)]/60 text-[var(--rammy-muted)] border border-[var(--rammy-line)]">Synthetic</span>
          </div>
          <div className="text-xs text-[var(--rammy-muted)] mt-1">
            Enrolled {v.created_at?.slice(0, 10)} · {v.sample_seconds}s sample · {v.usage_count} uses
            {v.has_source_recording ? " · source retained" : " · source deleted"}
          </div>
          <div className="text-[11px] text-[var(--rammy-muted)]/80 mt-1">
            Consent version: {v.provenance?.consent_version || "—"}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {v.is_paused ? (
            <button
              onClick={() => doAction("resume", `/api/myvoice/${v.handle}/resume`)}
              className="btn-pill h-8 px-3 text-xs border border-[var(--rammy-line)] inline-flex items-center gap-1"
              disabled={busy}
              data-testid={`myvoice-resume-${v.handle}`}
            >
              <Play size={12} /> Resume
            </button>
          ) : (
            <button
              onClick={() => doAction("pause", `/api/myvoice/${v.handle}/pause`)}
              className="btn-pill h-8 px-3 text-xs border border-[var(--rammy-line)] inline-flex items-center gap-1"
              disabled={busy}
              data-testid={`myvoice-pause-${v.handle}`}
            >
              <Pause size={12} /> Pause
            </button>
          )}
          <button
            onClick={() => onRerecord(v.handle)}
            className="btn-pill h-8 px-3 text-xs border border-[var(--rammy-line)] inline-flex items-center gap-1"
            data-testid={`myvoice-rerecord-${v.handle}`}
          >
            <RefreshCw size={12} /> Re-record
          </button>
          <button
            onClick={() => {
              if (window.confirm(`Delete "${v.name}"? This removes the voice everywhere and cannot be undone.`)) {
                doAction("delete", `/api/myvoice/${v.handle}`, "DELETE");
              }
            }}
            className="btn-pill h-8 px-3 text-xs bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1"
            disabled={busy}
            data-testid={`myvoice-delete-${v.handle}`}
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs">
        {v.has_source_recording && (
          <button
            onClick={() => {
              if (window.confirm("Delete the source recording? The voice will still work, but you won't be able to inspect the original audio.")) {
                doAction("source", `/api/myvoice/${v.handle}/source`, "DELETE");
              }
            }}
            className="text-[var(--rammy-muted)] hover:text-rose-700 inline-flex items-center gap-1"
            data-testid={`myvoice-delete-source-${v.handle}`}
          >
            <HardDrive size={12} /> Delete source recording
          </button>
        )}
        <button
          onClick={loadUsage}
          className="text-[var(--rammy-muted)] hover:text-[var(--rammy-text)] inline-flex items-center gap-1"
          data-testid={`myvoice-usage-${v.handle}`}
        >
          <MessageSquareWarning size={12} /> Usage history
        </button>
      </div>

      {showUsage && (
        <div className="mt-3 border-t border-[var(--rammy-line)] pt-3 text-xs" data-testid={`myvoice-usage-list-${v.handle}`}>
          {!usage ? (
            <div className="text-[var(--rammy-muted)]">Loading…</div>
          ) : (usage.history || []).length === 0 ? (
            <div className="text-[var(--rammy-muted)]">Never used yet.</div>
          ) : (
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {usage.history.slice().reverse().map((u, i) => (
                <li key={i} className="text-[var(--rammy-muted)]">
                  {u.ts?.slice(0, 19).replace("T", " ")} · {u.kind}
                  {u.chars ? ` · ${u.chars} chars` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Enrollment flow
// ═════════════════════════════════════════════════════════════════════════
function EnrollFlow({ rerecordHandle, onDone, onBack }) {
  const [step, setStep] = useState(0);         // 0 consent · 1 record · 2 finalizing · 3 preview
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(true);
  const [startError, setStartError] = useState(null);

  useEffect(() => {
    (async () => {
      setStarting(true);
      setStartError(null);
      try {
        const r = await jsonPost("/api/myvoice/enrollment/start",
                                 rerecordHandle ? { rerecord_of: rerecordHandle } : {});
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.detail || `Server error ${r.status}`);
        }
        setSession(await r.json());
      } catch (e) {
        setStartError(e.message);
      } finally {
        setStarting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerecordHandle]);

  if (starting) {
    return (
      <FlowShell onBack={onBack}>
        <div className="text-sm text-[var(--rammy-muted)] flex items-center gap-2">
          <Loader2 className="animate-spin" size={14} /> Preparing your session…
        </div>
      </FlowShell>
    );
  }
  if (startError) {
    return (
      <FlowShell onBack={onBack}>
        <div className="p-6 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 flex items-start gap-3">
          <ShieldAlert size={18} />
          <div>
            <div className="font-medium">Couldn&apos;t start enrollment</div>
            <div className="text-sm mt-1">{startError}</div>
            <button onClick={onBack} className="btn-pill h-8 px-3 text-xs mt-3 border border-rose-300">Back</button>
          </div>
        </div>
      </FlowShell>
    );
  }

  return (
    <FlowShell onBack={onBack} step={step}>
      {step === 0 && (
        <ConsentStep session={session} onNext={() => setStep(1)} />
      )}
      {step === 1 && (
        <RecordStep session={session}
                    onNext={() => setStep(2)}
                    onAbort={async () => {
                      await jsonPost(`/api/myvoice/enrollment/${session.session_id}/abort`);
                      onBack();
                    }} />
      )}
      {step === 2 && (
        <FinalizeStep session={session}
                      onNext={(candidate) => { setSession({ ...session, ...candidate }); setStep(3); }}
                      onBack={() => setStep(1)} />
      )}
      {step === 3 && (
        <PreviewStep session={session}
                     onActivated={() => { toast.success("Your voice is active."); onDone(); }}
                     onDiscard={async () => {
                       await jsonPost(`/api/myvoice/enrollment/${session.session_id}/abort`);
                       toast.info("Enrollment discarded.");
                       onDone();
                     }} />
      )}
    </FlowShell>
  );
}

function FlowShell({ children, onBack, step }) {
  const steps = ["Consent", "Record", "Verify", "Preview"];
  return (
    <div className="p-8 max-w-3xl mx-auto" data-testid="myvoice-enroll">
      <button onClick={onBack} className="text-sm text-[var(--rammy-muted)] hover:text-[var(--rammy-text)] mb-4 inline-flex items-center gap-1">
        <ChevronLeft size={14} /> Back to My Voice
      </button>
      <div className="mb-6 flex items-center gap-2 text-[11px] text-[var(--rammy-muted)]" data-testid="myvoice-enroll-progress">
        {steps.map((label, i) => (
          <div key={i} className={`flex items-center gap-1 ${i === step ? "text-[var(--rammy-text)] font-medium" : ""}`}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
              i < (step ?? 0) ? "bg-emerald-500 text-white" :
              i === step     ? "bg-[var(--rammy-cyan)] text-white" : "bg-[var(--rammy-line)]"
            }`}>
              {i < (step ?? 0) ? <CheckCircle2 size={12} /> : i + 1}
            </div>
            <span>{label}</span>
            {i < steps.length - 1 && <ArrowRight size={11} className="opacity-40 mx-1" />}
          </div>
        ))}
      </div>
      {children}
    </div>
  );
}

// ── Consent step ──────────────────────────────────────────────────────────
function ConsentStep({ session, onNext }) {
  const [authorized, setAuthorized] = useState(false);
  const [synthetic, setSynthetic] = useState(false);
  const [busy, setBusy] = useState(false);
  const consent = session.consent;

  const submit = async () => {
    if (!(authorized && synthetic)) return;
    setBusy(true);
    try {
      const r = await jsonPost(`/api/myvoice/enrollment/${session.session_id}/consent`, {
        agreed_authorized: true,
        agreed_synthetic: true,
        consent_version: consent.version,
        consent_hash: consent.hash,
      });
      if (!r.ok) {
        const j = await r.json();
        throw new Error(j.detail || "Consent failed");
      }
      onNext();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="myvoice-consent-step">
      <div>
        <h2 className="text-2xl font-semibold">{consent.title}</h2>
        <p className="text-sm text-[var(--rammy-muted)] mt-2 whitespace-pre-line">{consent.body}</p>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={authorized}
          onChange={(e) => setAuthorized(e.target.checked)}
          className="mt-1"
          data-testid="myvoice-consent-authorized"
        />
        <span className="text-sm">{consent.authorized_label}</span>
      </label>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={synthetic}
          onChange={(e) => setSynthetic(e.target.checked)}
          className="mt-1"
          data-testid="myvoice-consent-synthetic"
        />
        <span className="text-sm">{consent.synthetic_label}</span>
      </label>

      <div className="text-[11px] text-[var(--rammy-muted)]">
        Consent version {consent.version} · locale {consent.locale}. Your acceptance is recorded
        with a timestamp, IP, and user-agent for audit.
      </div>

      <div className="flex gap-2">
        <button
          onClick={submit}
          disabled={!(authorized && synthetic) || busy}
          className="btn-pill btn-primary h-10 px-4 inline-flex items-center gap-2 disabled:opacity-40"
          data-testid="myvoice-consent-continue"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : null}
          Agree &amp; continue
        </button>
      </div>
    </div>
  );
}

// ── Record step ───────────────────────────────────────────────────────────
function RecordStep({ session, onNext, onAbort }) {
  const [prompts] = useState(session.prompts || []);
  const [current, setCurrent] = useState(0);
  const [chunksState, setChunksState] = useState({}); // {index: {seconds}}
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const mrRef = useRef(null);
  const streamRef = useRef(null);
  const dataRef = useRef([]);
  const startedRef = useRef(0);
  const timerRef = useRef(null);

  const totalSeconds = useMemo(
    () => Object.values(chunksState).reduce((s, c) => s + (c.seconds || 0), 0),
    [chunksState],
  );
  const req = session.requirements || {};

  useEffect(() => () => cleanupMic(), []); // cleanup on unmount

  const cleanupMic = () => {
    try { mrRef.current?.stop(); } catch (_) { /* noop */ }
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) { /* noop */ }
    mrRef.current = null;
    streamRef.current = null;
    clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false, // preserve dynamics for cloning
        },
      });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mrRef.current = mr;
      dataRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) dataRef.current.push(e.data); };
      mr.onstop = () => upload(mime);
      mr.start();
      startedRef.current = Date.now();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed(Math.round((Date.now() - startedRef.current) / 1000));
      }, 250);
    } catch (e) {
      toast.error(`Microphone access denied: ${e.message}`);
    }
  };

  const stop = () => {
    if (!mrRef.current) return;
    try { mrRef.current.stop(); } catch (_) { /* noop */ }
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const upload = async (mime) => {
    const secs = (Date.now() - startedRef.current) / 1000;
    if (secs < (req.min_prompt_seconds || 6)) {
      toast.error(`Recording is too short — please record at least ${req.min_prompt_seconds}s.`);
      cleanupMic();
      return;
    }
    setUploading(true);
    const blob = new Blob(dataRef.current, { type: mime });
    const form = new FormData();
    form.append("index", String(current));
    form.append("seconds", String(secs.toFixed(2)));
    form.append("file", blob, `prompt-${current}.webm`);
    try {
      const r = await api(`/api/myvoice/enrollment/${session.session_id}/chunk`, {
        method: "POST",
        body: form,
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `Upload failed (${r.status})`);
      }
      const j = await r.json();
      setChunksState((c) => ({ ...c, [current]: { seconds: secs } }));
      // advance to the next unrecorded prompt.
      const nextUnrec = prompts.findIndex((p, i) => i !== current && !(chunksState[i] || (i === current)));
      // fallback: linear next
      const linear = current + 1 < prompts.length ? current + 1 : current;
      setCurrent(nextUnrec >= 0 ? nextUnrec : linear);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
      cleanupMic();
    }
  };

  const done = prompts.every((_, i) => chunksState[i]);
  const canFinalize = done && totalSeconds >= (req.min_total_seconds || 60);

  const finalize = async () => {
    setBusy(true);
    try {
      const r = await jsonPost(`/api/myvoice/enrollment/${session.session_id}/finalize`, {});
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        if (j.detail && typeof j.detail === "object") {
          // validation failure — surface actionable diagnostics
          throw new Error(_prettyValidation(j.detail));
        }
        throw new Error(j.detail || `Finalize failed (${r.status})`);
      }
      onNext(await r.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="myvoice-record-step">
      <div>
        <div className="text-sm text-[var(--rammy-muted)]">
          Prompt {current + 1} of {prompts.length} · total {totalSeconds.toFixed(0)}s / {req.min_total_seconds || 60}s
        </div>
        <div className="mt-2 text-lg leading-relaxed rounded-2xl p-4 bg-[var(--rm-surface)]/70 border border-[var(--rammy-line)]"
             data-testid={`myvoice-prompt-text-${current}`}>
          <div className="text-[11px] uppercase tracking-widest text-[var(--rammy-muted)] mb-1">Read aloud</div>
          {prompts[current]?.text}
        </div>
      </div>

      <div className="flex items-center gap-3">
        {!recording ? (
          <button
            onClick={start}
            disabled={uploading || busy}
            className="btn-pill btn-primary h-11 px-5 inline-flex items-center gap-2"
            data-testid="myvoice-record-start"
          >
            <Mic size={16} /> {chunksState[current] ? "Re-record this prompt" : "Start recording"}
          </button>
        ) : (
          <button
            onClick={stop}
            className="btn-pill h-11 px-5 inline-flex items-center gap-2 bg-rose-600 text-white"
            data-testid="myvoice-record-stop"
          >
            <StopCircle size={16} /> Stop ({elapsed}s)
          </button>
        )}
        {uploading && <span className="text-sm text-[var(--rammy-muted)] inline-flex items-center gap-1">
          <Loader2 size={14} className="animate-spin" /> Uploading…
        </span>}
      </div>

      <div className="grid grid-cols-5 gap-2" data-testid="myvoice-prompt-strip">
        {prompts.map((p, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`text-[11px] rounded-lg py-2 border transition ${
              i === current ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10" :
              chunksState[i] ? "border-emerald-400/40 bg-emerald-50" :
              "border-[var(--rammy-line)] bg-[var(--rm-surface)]/40"
            }`}
            data-testid={`myvoice-prompt-chip-${i}`}
          >
            <div className="font-medium">{i + 1}</div>
            <div className="text-[10px] mt-0.5">
              {chunksState[i] ? `${Math.round(chunksState[i].seconds)}s ✓` : "—"}
            </div>
          </button>
        ))}
      </div>

      <div className="flex justify-between items-center pt-2">
        <button onClick={onAbort} className="text-sm text-[var(--rammy-muted)] hover:text-rose-700">
          Abort &amp; delete recordings
        </button>
        <button
          onClick={finalize}
          disabled={!canFinalize || busy}
          className="btn-pill btn-primary h-10 px-4 inline-flex items-center gap-2 disabled:opacity-40"
          data-testid="myvoice-record-finalize"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : null}
          Continue
        </button>
      </div>
    </div>
  );
}

// ── Finalize / validation step ────────────────────────────────────────────
function FinalizeStep({ session, onNext, onBack }) {
  // In the current flow the RecordStep already triggered finalize and
  // captured the response, so we transition straight to preview via a small
  // "verifying" screen for visual continuity.
  useEffect(() => {
    // If we already have preview_ready in session, skip immediately.
    if (session.candidate_handle) {
      onNext({});
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="text-center py-16" data-testid="myvoice-finalize-step">
      <Loader2 className="mx-auto animate-spin text-[var(--rammy-cyan)]" size={32} />
      <div className="mt-3 text-sm text-[var(--rammy-muted)]">Verifying your recording…</div>
      <button onClick={onBack} className="mt-6 text-xs text-[var(--rammy-muted)] underline">Back to recording</button>
    </div>
  );
}

// ── Preview step ──────────────────────────────────────────────────────────
function PreviewStep({ session, onActivated, onDiscard }) {
  const audioRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [keepSource, setKeepSource] = useState(false);

  const previewUrl = `${BACKEND}/api/myvoice/enrollment/${session.session_id}/preview`;

  const activate = async () => {
    setBusy(true);
    try {
      const r = await jsonPost(`/api/myvoice/enrollment/${session.session_id}/activate`, {
        keep_source_recording: keepSource,
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Activation failed");
      }
      onActivated();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="myvoice-preview-step">
      <div>
        <h2 className="text-2xl font-semibold">Preview your new voice</h2>
        <p className="text-sm text-[var(--rammy-muted)] mt-2 max-w-lg">
          Rammy just spoke a short line in your voice. Listen carefully. If it sounds off,
          you can discard and try again — nothing is activated yet.
        </p>
      </div>

      <div className="rounded-2xl border border-[var(--rammy-line)] bg-[var(--rm-surface)]/70 p-4 flex items-center gap-4">
        <div className="w-11 h-11 rounded-full bg-[var(--rammy-cyan)]/15 flex items-center justify-center">
          <Play className="text-[var(--rammy-cyan)]" size={18} />
        </div>
        <audio ref={audioRef} src={previewUrl} controls className="flex-1"
               data-testid="myvoice-preview-audio" preload="auto" />
      </div>

      <label className="flex items-start gap-3 cursor-pointer text-sm">
        <input type="checkbox" checked={keepSource}
               onChange={(e) => setKeepSource(e.target.checked)}
               className="mt-1" data-testid="myvoice-keep-source" />
        <span>
          Keep my source recording for audit/re-record.
          <span className="block text-xs text-[var(--rammy-muted)] mt-0.5">
            If unchecked, the raw audio is deleted after activation. The voice itself remains active.
          </span>
        </span>
      </label>

      <div className="flex justify-between gap-3">
        <button onClick={onDiscard} className="btn-pill h-10 px-4 border border-[var(--rammy-line)] text-sm inline-flex items-center gap-1" data-testid="myvoice-preview-discard">
          <XCircle size={14} /> Discard
        </button>
        <button
          onClick={activate}
          disabled={busy}
          className="btn-pill btn-primary h-10 px-5 inline-flex items-center gap-2 disabled:opacity-40"
          data-testid="myvoice-preview-activate"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          Activate this voice
        </button>
      </div>
    </div>
  );
}

// Turn the finalize validation dict into a human sentence.
function _prettyValidation(detail) {
  if (!detail || detail === true) return "Recording didn't pass verification.";
  const reasons = detail.reasons || [];
  if (reasons.includes("multiple_speakers")) {
    return "We heard more than one voice in your recording. Please record alone, in a quiet space.";
  }
  if (reasons.includes("missing_challenge_tokens")) {
    return "Your recording is missing some of the challenge words or numbers. Please re-record the highlighted prompts word-for-word.";
  }
  if (reasons.includes("prompt_coverage_low")) {
    return "Your recording doesn't closely match the prompt text. Please read the prompts as written.";
  }
  if (reasons.includes("insufficient_speech")) {
    return "We couldn't hear enough of your voice. Please re-record with clearer speech.";
  }
  return "Verification failed — please re-record and try again.";
}
